import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "./axios";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

// ── Types ────────────────────────────────────────────────────────────────────

export interface StationImportStats {
  fuel_stations: number;
  ev_stations: number;
  total_signs: number;
  last_fuel_sync: string | null;
  last_ev_sync: string | null;
}

export interface ImportResult {
  created: number;
  updated: number;
  skipped: number;
  errors: number;
  total_fetched: number;
}

export interface ImportResponse {
  success: boolean;
  message: string;
  data: {
    fuel?: ImportResult;
    ev?: ImportResult;
  };
}

// ── Query keys ───────────────────────────────────────────────────────────────

export const stationKeys = {
  stats: ["station-import", "stats"] as const,
};

// ── Hooks ────────────────────────────────────────────────────────────────────

/**
 * Fetch station import statistics (counts + last sync timestamps).
 */
export const useStationStats = () => {
  const { data, ...rest } = useQuery({
    queryKey: stationKeys.stats,
    queryFn: async (): Promise<StationImportStats> => {
      const response = await axios.get("/admin/stations/stats");
      return response.data?.data ?? response.data;
    },
    refetchInterval: 30_000, // refresh every 30s while visible
  });

  return { stats: data ?? null, ...rest };
};

/**
 * Trigger a station import.
 * @param source "fuel" | "ev" | "all"
 */
export const useImportStations = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (source: "fuel" | "ev" | "all" = "all"): Promise<ImportResponse> => {
      const response = await axios.post("/admin/stations/import", { source });
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: stationKeys.stats });

      const fuel = data.data?.fuel;
      const ev = data.data?.ev;
      const parts: string[] = [];

      if (fuel) {
        parts.push(
          t("stations.fuelResult", {
            created: fuel.created,
            updated: fuel.updated,
            defaultValue: `Carburants: ${fuel.created} créés, ${fuel.updated} mis à jour`,
          })
        );
      }
      if (ev) {
        parts.push(
          t("stations.evResult", {
            created: ev.created,
            updated: ev.updated,
            defaultValue: `Bornes EV: ${ev.created} créées, ${ev.updated} mises à jour`,
          })
        );
      }

      toast.success(parts.join(" | ") || t("stations.importSuccess", "Import terminé"));
    },
    onError: () => {
      toast.error(t("stations.importError", "Erreur lors de l'import des stations"));
    },
  });
};
